import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { SignOut, Trash, EnvelopeOpen, Envelope, ArrowClockwise } from "@phosphor-icons/react";
import { LogoMark } from "@/components/Logo";
import { api, useAuth } from "@/context/AuthContext";
import { SITE } from "@/constants/site";

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id, status) => {
    await api.patch(`/enquiries/${id}`, { status });
    setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
  };

  const remove = async (id) => {
    await api.delete(`/enquiries/${id}`);
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
    toast.success("Enquiry deleted");
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    navigate("/admin");
  };

  const newCount = enquiries.filter((e) => e.status === "new").length;

  return (
    <div className="min-h-screen bg-sage" data-testid="admin-dashboard">
      <header className="bg-white/80 backdrop-blur-xl border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LogoMark className="w-8 h-8" />
            <span className="font-serif font-semibold text-ink">{SITE.brand} · Enquiries</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-button"
              className="p-2.5 rounded-full text-moss hover:bg-sage transition-colors duration-300" aria-label="Refresh">
              <ArrowClockwise size={18} weight="bold" />
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm font-semibold text-forest border border-forest/20 px-4 py-2 rounded-full hover:bg-mint-pale transition-colors duration-300">
              <SignOut size={16} weight="bold" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl text-ink">Enquiries</h1>
            <p className="mt-2 text-sm text-moss" data-testid="admin-enquiry-count">
              {enquiries.length} total · {newCount} new
            </p>
          </div>
        </div>

        {loading ? (
          <p className="mt-16 text-center text-moss" data-testid="admin-loading">Loading enquiries...</p>
        ) : enquiries.length === 0 ? (
          <div className="mt-16 text-center bg-white rounded-3xl border border-black/5 p-16" data-testid="admin-empty-state">
            <p className="font-serif text-2xl text-ink">No enquiries yet</p>
            <p className="mt-2 text-sm text-moss">New website enquiries will appear here.</p>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-4">
            {enquiries.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="bg-white rounded-2xl border border-black/5 p-6 flex flex-wrap gap-6 items-start justify-between"
                data-testid={`enquiry-row-${e.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-ink">{e.name}</p>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${e.status === "new" ? "bg-mint-pale text-forest" : "bg-sage text-moss"}`}
                      data-testid={`enquiry-status-${e.id}`}>
                      {e.status === "new" ? "New" : "Read"}
                    </span>
                    <span className="text-xs bg-sage text-moss px-2.5 py-1 rounded-full">{e.service}</span>
                  </div>
                  <p className="mt-2 text-sm text-moss">
                    <a href={`tel:${e.phone}`} className="font-semibold text-forest">{e.phone}</a>
                    {e.email && <> · {e.email}</>}
                  </p>
                  {e.message && <p className="mt-2 text-sm text-ink/80 max-w-xl">{e.message}</p>}
                  <p className="mt-2 text-xs text-moss/70">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => markRead(e.id, e.status === "new" ? "read" : "new")}
                    data-testid={`enquiry-toggle-read-${e.id}`}
                    className="p-2.5 rounded-full text-moss hover:bg-sage transition-colors duration-300"
                    aria-label={e.status === "new" ? "Mark as read" : "Mark as new"}>
                    {e.status === "new" ? <EnvelopeOpen size={18} /> : <Envelope size={18} />}
                  </button>
                  <button onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`}
                    className="p-2.5 rounded-full text-red-500 hover:bg-red-50 transition-colors duration-300" aria-label="Delete enquiry">
                    <Trash size={18} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
